// CreateMedia.js
// Multi-upload + drag & drop
// Pinterest-like layout: left preview, center form, right vertical list of medias

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 200;
const MAX_FILES = 10;

const CM_LANG_KEY = "weavevo_lang";
const AI_ENGINE = "Gemini";

const CM_I18N = {
  en: {
    // General
    close: "Close",
    dash: "—",
    untitled: "Untitled",
    alt_preview: "preview",
    alt_thumb: "thumbnail",

    // Header
    header_title: "Create media",
    header_sub:
      "Upload multiple images or videos, then customize the details of each media before publishing it.",
    header_ai_line:
      "AI Content Guard analyzes your content with {engine} (LLM) and applies safety requirements.",

    // Dropzone
    drop_main: "Drag & drop your files here",
    drop_or: "or",
    drop_choose: "Choose files",
    drop_limits: "Images (max {img} MB) · Videos (max {vid} MB)",

    // Form
    label_title: "Title",
    ph_title: "Add a descriptive title",
    hint_select_media: "Select or add a media to fill in its details.",
    label_description: "Description",
    ph_description: "Add a detailed description, context, emotions…",
    label_tags: "Tags",
    ph_tags: "e.g., travel, behind-the-scenes… (comma-separated)",
    label_visibility: "Visibility",
    visibility_public: "Public",
    visibility_private: "Private",
    visibility_help:
      "Public: visible to everyone · Private: visible only to you (or per app rules).",
    public_disabled_note:
      "Public disabled: not compliant with safety requirements. You can save this media as Private.",

    // Batch progress
    batch_title: "Analyzing & uploading",
    batch_in_progress: "In progress:",
    batch_engine_info: "{engine} checks safety compliance and generates a recap.",

    // AI card
    ai_card_title: "AI Content Guard",
    ai_card_subline:
      "Compliance check + recap. Result is shown here (no tooltip).",
    ai_engine_badge: "{engine} · LLM",

    ai_badge_scanning: "Analyzing",
    ai_badge_allow: "Compliant",
    ai_badge_review: "Not compliant (Public)",
    ai_badge_block: "Not compliant (Public)",
    ai_badge_error: "AI error",
    ai_badge_idle: "Not analyzed",

    ai_scanning_title: "{engine} analysis in progress…",
    ai_scanning_desc:
      "We check safety compliance and generate a clear recap (reasons, suggested tags, score).",
    ai_steps: "Steps: Upload → {engine} scan → Verdict → Recap",

    ai_allow_title: "Compliant: public posting allowed",
    ai_allow_desc:
      "You can publish as Public or Private. The {engine} recap is displayed below.",

    ai_block_title: "Public posting blocked (safety requirements)",
    ai_block_desc_prefix: "You can add this media, but only as",
    ai_block_desc_suffix:
      "Public visibility is disabled because the content is not compliant.",

    btn_set_private: "Set to Private",
    btn_view_my_media: "View my media",
    toast_set_private_ok: "OK — visibility set to Private.",

    kpi_decision: "Decision",
    kpi_risk: "Estimated risk",
    ai_summary_title: "{engine} summary",
    ai_points_title: "Detected points",
    ai_flags_title: "Signals / flags",
    ai_suggested_tags_title: "Suggested tags ({engine})",
    btn_apply_suggested_tags: "Apply suggested tags",
    reminder_prefix: "Reminder: this media can only be kept as",
    reminder_suffix: "(safety requirements).",

    ai_idle_title: "Ready for {engine} analysis",
    ai_idle_desc_1:
      "When you publish, {engine} analyzes the media and shows a verdict + recap here.",
    ai_idle_desc_2:
      "If not compliant, Public will be disabled and Private will remain available.",

    // Footer
    footer_ready: "{count} media{plural} ready to be published.",
    footer_add_to_start: "Add media to get started.",
    btn_reset: "Reset",
    btn_publish_single: "Publish media",
    btn_publish_multi: "Publish media",

    // Right panel
    right_title: "Added media",
    right_sub: "Click to select a media to edit.",
    right_count: "{count} item{plural}",
    right_empty:
      "No media yet. Add a file on the left to see it here.",
    badge_image: "Image",
    badge_video: "Video",
    badge_public_off: "Public OFF",
    aria_view_ai: "View {engine} analysis",
    aria_remove_file: "Remove this file",

    // Units
    unit_mb: "MB",

    // Validation / errors / toasts
    err_add_at_least_one: "Add at least one file.",
    err_unsupported_type:
      "Unsupported type. Only images and videos are accepted.",
    err_some_images_too_big: "Some images exceed {max} MB.",
    err_some_videos_too_big: "Some videos exceed {max} MB.",
    err_max_files: "You can add up to {max} files at a time.",
    err_need_login: "You must be logged in to publish media.",
    err_network_retry: "Network error. Please try again.",
    err_invalid_server_response: "Invalid server response.",
    err_upload_failed: "Upload error.",
    err_public_disabled_toast:
      "Public posting disabled: this content is not compliant with safety requirements. You can add it as Private.",
    reason_public_disabled:
      "Not compliant with safety requirements for public posting.",
    toast_saved_as_private:
      "This content cannot be posted publicly (safety). It has been saved as Private.",
    toast_published_single: "Media published successfully.",
    toast_published_multi: "Media published successfully.",
    toast_analysis_done_some_blocked:
      "Analysis complete: some content cannot be posted publicly (safety). See the “AI Content Guard” recap.",
    toast_some_added_private:
      "Some media were added (as Private if needed).",
    toast_done_default: "Publishing completed.",
  },

  fr: {
    // General
    close: "Fermer",
    dash: "—",
    untitled: "Untitled",
    alt_preview: "preview",
    alt_thumb: "thumbnail",

    // Header
    header_title: "Créer un média",
    header_sub:
      "Ajoutez plusieurs images ou vidéos, puis personnalisez les détails de chaque média avant de le publier.",
    header_ai_line:
      "AI Content Guard analyse votre contenu avec {engine} (LLM) et applique les exigences sécurité.",

    // Dropzone
    drop_main: "Glissez-déposez vos fichiers ici",
    drop_or: "ou",
    drop_choose: "Choisir des fichiers",
    drop_limits: "Images (max {img} Mo) · Vidéos (max {vid} Mo)",

    // Form
    label_title: "Titre",
    ph_title: "Ajoutez un titre descriptif",
    hint_select_media: "Sélectionnez ou ajoutez un média pour renseigner ses détails.",
    label_description: "Description",
    ph_description: "Ajoutez une description détaillée, un contexte, des émotions…",
    label_tags: "Tags",
    ph_tags: "Ex : voyage, coulisses… (séparés par des virgules)",
    label_visibility: "Visibilité",
    visibility_public: "Public",
    visibility_private: "Privé",
    visibility_help:
      "Public : visible pour tous · Privé : visible uniquement pour vous (ou selon les règles de l’app).",
    public_disabled_note:
      "Public désactivé : non conforme aux exigences de sécurité. Vous pouvez enregistrer ce média en Privé.",

    // Batch progress
    batch_title: "Analyse & téléversement en cours",
    batch_in_progress: "En cours :",
    batch_engine_info: "{engine} vérifie la conformité sécurité et génère un récapitulatif.",

    // AI card
    ai_card_title: "AI Content Guard",
    ai_card_subline:
      "Analyse de conformité + récapitulatif. Résultat visible ici (pas de tooltip).",
    ai_engine_badge: "{engine} · LLM",

    ai_badge_scanning: "Analyse en cours",
    ai_badge_allow: "Conforme",
    ai_badge_review: "Non conforme (Public)",
    ai_badge_block: "Non conforme (Public)",
    ai_badge_error: "Erreur IA",
    ai_badge_idle: "Non analysé",

    ai_scanning_title: "Analyse {engine} en cours…",
    ai_scanning_desc:
      "Nous vérifions la conformité sécurité et générons un récapitulatif clair (raisons, tags suggérés, score).",
    ai_steps: "Étapes : Upload → Scan {engine} → Verdict → Récapitulatif",

    ai_allow_title: "Conforme : publication publique autorisée",
    ai_allow_desc:
      "Vous pouvez publier en Public ou Privé. Le récapitulatif {engine} est affiché ci-dessous.",

    ai_block_title: "Publication publique interdite (exigences sécurité)",
    ai_block_desc_prefix: "Vous pouvez ajouter ce média, mais uniquement en",
    ai_block_desc_suffix:
      "La visibilité Public est désactivée car le contenu n’est pas conforme.",

    btn_set_private: "Mettre en Privé",
    btn_view_my_media: "Voir mes médias",
    toast_set_private_ok: "OK — visibilité réglée sur Privé.",

    kpi_decision: "Décision",
    kpi_risk: "Risque estimé",
    ai_summary_title: "Synthèse {engine}",
    ai_points_title: "Points détectés",
    ai_flags_title: "Signaux / flags",
    ai_suggested_tags_title: "Tags suggérés ({engine})",
    btn_apply_suggested_tags: "Appliquer les tags suggérés",
    reminder_prefix: "Rappel : ce média peut être conservé en",
    reminder_suffix: "uniquement (exigences sécurité).",

    ai_idle_title: "Prêt pour l’analyse {engine}",
    ai_idle_desc_1:
      "Lors de la publication, {engine} analyse le média et affiche ici un verdict + un récapitulatif.",
    ai_idle_desc_2:
      "Si non conforme, Public sera désactivé et Privé restera possible.",

    // Footer
    footer_ready:
      "{count} média{plural_m} prêt{plural_p} à être publié{plural_b}.",
    footer_add_to_start: "Ajoutez des médias pour commencer.",
    btn_reset: "Réinitialiser",
    btn_publish_single: "Publier le média",
    btn_publish_multi: "Publier les médias",

    // Right panel
    right_title: "Médias ajoutés",
    right_sub: "Cliquez pour sélectionner un média à éditer.",
    right_count: "{count} élément{plural}",
    right_empty:
      "Aucun média pour l’instant. Ajoutez un fichier à gauche pour le voir ici.",
    badge_image: "Image",
    badge_video: "Vidéo",
    badge_public_off: "Public désactivé",
    aria_view_ai: "Voir l’analyse {engine}",
    aria_remove_file: "Supprimer ce fichier",

    // Units
    unit_mb: "Mo",

    // Validation / errors / toasts
    err_add_at_least_one: "Ajoutez au moins un fichier.",
    err_unsupported_type:
      "Type non supporté. Seules les images et les vidéos sont acceptées.",
    err_some_images_too_big: "Certaines images dépassent {max} Mo.",
    err_some_videos_too_big: "Certaines vidéos dépassent {max} Mo.",
    err_max_files: "Vous pouvez ajouter au maximum {max} fichiers à la fois.",
    err_need_login: "Vous devez être connecté pour publier un média.",
    err_network_retry: "Erreur réseau. Veuillez réessayer.",
    err_invalid_server_response: "Réponse invalide du serveur.",
    err_upload_failed: "Erreur lors du téléversement.",
    err_public_disabled_toast:
      "Publication publique désactivée : ce contenu n’est pas conforme aux exigences de sécurité. Vous pouvez l’ajouter en Privé.",
    reason_public_disabled:
      "Non conforme aux exigences de sécurité pour une publication publique.",
    toast_saved_as_private:
      "Ce contenu ne peut pas être publié en Public (sécurité). Il a été enregistré en Privé.",
    toast_published_single: "Média publié avec succès.",
    toast_published_multi: "Médias publiés avec succès.",
    toast_analysis_done_some_blocked:
      "Analyse terminée : certains contenus ne peuvent pas être publiés en Public (sécurité). Consultez le récapitulatif “AI Content Guard”.",
    toast_some_added_private:
      "Certains médias ont été ajoutés (en Privé si nécessaire).",
    toast_done_default: "Publication terminée.",
  },
};

const getInitialLang = () => {
  try {
    const raw =
      localStorage.getItem(CM_LANG_KEY) ||
      localStorage.getItem("weavevo_language") ||
      "";
    const norm = String(raw || "").toLowerCase().slice(0, 2);
    return norm === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
};



const createMediaStyles = `
.cm-page {
  padding-top: 90px;
  padding-bottom: 40px;
  background-color: #f8fafc;
}

@media (max-width: 767.98px) {
  .cm-page {
    padding-top: 80px;
    padding-bottom: 24px;
  }
}

/* Dropzone */
.cm-dropzone {
  border-radius: 1.5rem;
  border: 2px dashed #cbd5e1;
  background: #f8fafc;
  padding: 1.5rem;
  text-align: center;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.15s ease;
  cursor: pointer;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.cm-dropzone.dragover {
  background-color: #e0f2fe;
  border-color: #0d6efd;
  transform: translateY(-1px);
}

.cm-drop-icon {
  font-size: 2.25rem;
  margin-bottom: 0.5rem;
}

.cm-drop-main {
  font-weight: 600;
  font-size: 0.98rem;
}

.cm-drop-sub {
  font-size: 0.85rem;
  color: #64748b;
}

/* Primary preview */
.cm-primary-preview {
  border-radius: 1rem;
  overflow: hidden;
  background: #020617;
  margin-bottom: 0.75rem;
  position: relative;
  aspect-ratio: 16 / 9;
}

.cm-primary-preview img,
.cm-primary-preview video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* RIGHT PANEL – list of medias */
.cm-drafts-panel {
  background: #f9fafb;
  border-radius: 1.25rem;
  border: 1px solid #e5e7eb;
  padding: 0.75rem 0.75rem 0.75rem;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.cm-drafts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem 0.5rem;
}

.cm-drafts-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.cm-drafts-sub {
  font-size: 0.78rem;
  color: #6b7280;
}

.cm-drafts-count {
  font-size: 0.78rem;
}

.cm-file-list {
  margin-top: 0.35rem;
  padding: 0.25rem;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.cm-file-item {
  border-radius: 0.75rem;
  padding: 0.35rem 0.6rem;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.cm-file-item + .cm-file-item {
  margin-top: 0.4rem;
}

.cm-file-item.active {
  border-color: #0d6efd;
  background-color: #eff6ff;
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.3);
}

.cm-file-item-main {
  flex: 1;
  min-width: 0;
}

.cm-file-thumb {
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
}

.cm-file-thumb img,
.cm-file-thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.cm-file-name {
  font-size: 0.82rem;
  font-weight: 500;
  color: #0f172a;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cm-file-meta {
  font-size: 0.75rem;
  color: #64748b;
}

/* Tags input */
.cm-tags-input {
  border-radius: 999px;
}

/* Visibility toggle */
.cm-vis-toggle {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px;
  background-color: #e5e7eb;
}

.cm-vis-btn {
  border: none;
  background: transparent;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cm-vis-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cm-vis-btn.active-public {
  background-color: #0d6efd;
  color: #ffffff;
}

.cm-vis-btn.active-private {
  background-color: #111827;
  color: #ffffff;
}

/* Errors & toasts */
.cm-error {
  font-size: 0.8rem;
  color: #dc2626;
  margin-top: 0.25rem;
}

.cm-toast {
  position: fixed;
  right: 16px;
  bottom: 16px;
  padding: 10px 14px;
  border-radius: 12px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.28);
  font-size: .85rem;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2000;
  color: #fff;
}

.cm-toast-success { background: #16a34a; }
.cm-toast-error { background: #dc2626; }

.cm-toast button {
  border: none;
  background: transparent;
  color: inherit;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
}

/* AI Content Guard */
.cm-ai-card {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 1rem;
  padding: 0.9rem 0.95rem;
}

.cm-ai-card-flash {
  animation: cmFlash 0.9s ease-out 1;
}

@keyframes cmFlash {
  0% { box-shadow: 0 0 0 0 rgba(13,110,253,.0); transform: translateY(0); }
  30% { box-shadow: 0 0 0 4px rgba(13,110,253,.18); transform: translateY(-1px); }
  100% { box-shadow: 0 0 0 0 rgba(13,110,253,.0); transform: translateY(0); }
}

.cm-ai-badge {
  font-size: 0.75rem;
  border-radius: 999px;
  padding: 0.25rem 0.55rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid transparent;
}

.cm-ai-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  display: inline-block;
}

.cm-ai-ok { background: #dcfce7; color: #14532d; border-color: #86efac; }
.cm-ai-review { background: #fef9c3; color: #713f12; border-color: #fde047; }
.cm-ai-block { background: #fee2e2; color: #7f1d1d; border-color: #fca5a5; }
.cm-ai-scan { background: #e0f2fe; color: #0c4a6e; border-color: #7dd3fc; }
.cm-ai-err { background: #f1f5f9; color: #334155; border-color: #cbd5e1; }

.cm-ai-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 0.78rem;
}

.cm-ai-subline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  flex-wrap: wrap;
  margin-top: .55rem;
}

.cm-ai-engine {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #0b1220;
  color: #e5e7eb;
  border: 1px solid rgba(148,163,184,.25);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: .78rem;
}

.cm-ai-engine i { opacity: .9; }

.cm-ai-callout {
  border-radius: .85rem;
  padding: .75rem .85rem;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.cm-ai-callout.ok {
  border-color: #86efac;
  background: #ecfdf5;
  color: #065f46;
}

.cm-ai-callout.warn {
  border-color: #fde047;
  background: #fffbeb;
  color: #92400e;
}

.cm-ai-callout.danger {
  border-color: #fca5a5;
  background: #fef2f2;
  color: #7f1d1d;
}

.cm-ai-mini {
  font-size: .82rem;
  color: #64748b;
}

.cm-ai-kv {
  display: grid;
  grid-template-columns: 1fr;
  gap: .6rem;
  margin-top: .75rem;
}

@media (min-width: 992px) {
  .cm-ai-kv { grid-template-columns: 1fr 1fr; }
}

.cm-ai-kv-box {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: .85rem;
  padding: .7rem .75rem;
}

.cm-ai-kv-title {
  font-size: .78rem;
  color: #64748b;
  margin-bottom: .25rem;
}

.cm-ai-kv-value {
  font-size: .9rem;
  color: #0f172a;
  font-weight: 600;
}

.cm-ai-progress {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.cm-ai-progress > span {
  display: block;
  height: 100%;
  width: 40%;
  border-radius: 999px;
  background: #0d6efd;
  animation: cmIndeterminate 1.25s ease-in-out infinite;
}

@keyframes cmIndeterminate {
  0% { transform: translateX(-120%); }
  100% { transform: translateX(280%); }
}

.cm-batchbar {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 1rem;
  padding: .75rem .85rem;
  margin-bottom: 1rem;
}

.cm-batchbar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  flex-wrap: wrap;
}

.cm-batchbar-title {
  font-weight: 600;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: .95rem;
}

.cm-batchbar-sub {
  font-size: .82rem;
  color: #64748b;
  margin-top: .35rem;
}

.cm-batchbar-meter {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
  margin-top: .6rem;
}

.cm-batchbar-meter > span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #16a34a;
  width: 0%;
  transition: width .25s ease;
}

.cm-info-btn {
  border: none;
  background: transparent;
  color: #64748b;
  padding: 2px 6px;
  border-radius: 10px;
  line-height: 1;
}

.cm-info-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}
`;

function fileNameSansExt(name = "") {
  const i = name.lastIndexOf(".");
  return i === -1 ? name : name.slice(0, i);
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function normalizeRiskScore(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return null;
  let score = v;
  if (score <= 1) score = Math.round(score * 100);
  else score = Math.round(score);
  return clamp(score, 0, 100);
}

const CreateMedia = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [lang, setLang] = useState(getInitialLang());

  // Keep language in sync with SettingsPage (localStorage key: weavevo_lang)
  useEffect(() => {
    const sync = () => {
      const next = getInitialLang();
      setLang(next);
      try {
        document.documentElement.lang = next;
      } catch {}
    };

    const onStorage = (ev) => {
      if (!ev || !ev.key) return;
      if (ev.key === CM_LANG_KEY || ev.key === "weavevo_language") sync();
    };

    sync();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const dict = useMemo(() => CM_I18N[lang] || CM_I18N.en, [lang]);

  const t = (key, vars = {}) => {
    const template = dict?.[key] ?? CM_I18N.en?.[key] ?? key;
    return String(template).replace(/\{(\w+)\}/g, (_, k) =>
      Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`
    );
  };


  // { id, file, previewURL, sizeMB, kind, title, description, tags, visibility }
  const [files, setFiles] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [toastSuccess, setToastSuccess] = useState("");
  const [toastError, setToastError] = useState("");

  const [batchProgress, setBatchProgress] = useState({
    running: false,
    current: 0,
    total: 0,
    currentTitle: "",
  });

  const [aiFlash, setAiFlash] = useState(false);

  const dropRef = useRef(null);
  const fileInputRef = useRef(null);
  const aiCardRef = useRef(null);

  useEffect(() => {
    if (!aiFlash) return;
    const t = setTimeout(() => setAiFlash(false), 900);
    return () => clearTimeout(t);
  }, [aiFlash]);

  const activeEntry = useMemo(
    () => files.find((f) => f.id === activeId) || null,
    [files, activeId]
  );

  const primary = activeEntry || files[0] || null;

  const isRestrictedForPublic = (entry) => {
    const st = String(entry?.aiStatus || "").toLowerCase();
    // Interprétation UX : si ce n'est pas "allow", la visibilité publique peut être désactivée
    return st === "review" || st === "block";
  };

  const aiMetaFor = (entry) => {
    const st = (entry?.aiStatus || "idle").toLowerCase();
    if (st === "scanning") return { cls: "cm-ai-badge cm-ai-scan", dot: "#0284c7", label: t("ai_badge_scanning") };
    if (st === "allow") return { cls: "cm-ai-badge cm-ai-ok", dot: "#16a34a", label: t("ai_badge_allow") };
    if (st === "review") return { cls: "cm-ai-badge cm-ai-review", dot: "#ca8a04", label: t("ai_badge_review") };
    if (st === "block") return { cls: "cm-ai-badge cm-ai-block", dot: "#dc2626", label: t("ai_badge_review") };
    if (st === "error") return { cls: "cm-ai-badge cm-ai-err", dot: "#334155", label: t("ai_badge_error") };
    if (st === "idle") return { cls: "cm-ai-badge cm-ai-err", dot: "#94a3b8", label: t("ai_badge_idle") };
    return { cls: "cm-ai-badge cm-ai-err", dot: "#94a3b8", label: t("dash") };
  };

  const focusAiRecap = () => {
    setAiFlash(true);
    try {
      aiCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (_) {
      // ignore
    }
  };

  const applySuggestedTags = () => {
    if (!activeEntry) return;
    const suggested = Array.isArray(activeEntry.aiContentTypes) ? activeEntry.aiContentTypes : [];
    if (!suggested.length) return;

    const current = String(activeEntry.tags || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const merged = Array.from(new Set([...current, ...suggested]))
      .slice(0, 12)
      .join(", ");

    updateActiveField("tags", merged);
  };

  const lockToPrivate = (id, lockReason = "") => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === id
          ? {
              ...f,
              visibility: "PRIVATE",
              visibilityLocked: true,
              visibilityLockReason: lockReason || f.visibilityLockReason || "",
            }
          : f
      )
    );
  };

  const updateActiveField = (field, value) => {
    if (!activeId) return;

    // Guard: empêcher PUBLIC si le contenu n'est pas conforme au niveau sécurité
    if (field === "visibility" && value === "PUBLIC") {
      const entry = files.find((f) => f.id === activeId);
      const locked = !!entry?.visibilityLocked || isRestrictedForPublic(entry);
      if (locked) {
        setToastError(
          t("err_public_disabled_toast")
        );
        focusAiRecap();
        // force PRIVATE
        lockToPrivate(activeId, entry?.visibilityLockReason || "");
        return;
      }
    }

    setFiles((prev) => prev.map((f) => (f.id === activeId ? { ...f, [field]: value } : f)));
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) dropRef.current.classList.add("dragover");
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) dropRef.current.classList.remove("dragover");
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) dropRef.current.classList.remove("dragover");

    const fileList = Array.from(e.dataTransfer?.files || []);
    if (fileList.length) {
      handleFilesSelected(fileList);
    }
  };

  const handleFileInputChange = (e) => {
    const fileList = Array.from(e.target.files || []);
    if (fileList.length) {
      handleFilesSelected(fileList);
    }
    e.target.value = "";
  };

  const handleFilesSelected = (fileList) => {
    const currentCount = files.length;
    const maxRemaining = MAX_FILES - currentCount;
    const slice = fileList.slice(0, maxRemaining);

    let fileError = "";
    const newEntries = [];

    slice.forEach((file) => {
      const type = file.type || "";
      const isImage = type.startsWith("image/");
      const isVideo = type.startsWith("video/");

      if (!isImage && !isVideo) {
        fileError = t("err_unsupported_type");
        return;
      }

      const sizeMB = file.size / (1024 * 1024);
      if (isImage && sizeMB > MAX_IMAGE_MB) {
        fileError = t("err_some_images_too_big", { max: MAX_IMAGE_MB });
        return;
      }
      if (isVideo && sizeMB > MAX_VIDEO_MB) {
        fileError = t("err_some_videos_too_big", { max: MAX_VIDEO_MB });
        return;
      }

      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const previewURL = URL.createObjectURL(file);

      newEntries.push({
        id,
        file,
        previewURL,
        sizeMB,
        kind: isImage ? "image" : "video",
        title: fileNameSansExt(file.name),
        description: "",
        tags: "",
        visibility: "PUBLIC",

        // Visibility lock when non compliant for Public
        visibilityLocked: false,
        visibilityLockReason: "",

        // --- AI Content Guard (Gemini) ---
        aiStatus: "idle", // idle | scanning | allow | review | block | error
        aiDecision: null,
        aiRiskScore: null,
        aiFlags: [],
        aiReasons: [],
        aiContentTypes: [],
        aiSummary: "",

        uploadedId: null,
        uploadedAt: null,
        uploadError: "",
      });
    });

    if (fileList.length > maxRemaining) {
      fileError = t("err_max_files", { max: MAX_FILES });
    }

    if (fileError) {
      setErrors((prev) => ({ ...prev, files: fileError }));
      return;
    }

    if (!newEntries.length) return;

    setFiles((prev) => {
      const merged = [...prev, ...newEntries];
      if (!activeId && merged.length) {
        setActiveId(merged[0].id);
      }
      return merged;
    });

    setErrors((prev) => ({ ...prev, files: "" }));
  };

  const handleRemoveFile = (id) => {
    setFiles((prev) => {
      const entry = prev.find((f) => f.id === id);
      if (entry?.previewURL) {
        URL.revokeObjectURL(entry.previewURL);
      }
      const next = prev.filter((f) => f.id !== id);
      if (id === activeId) {
        setActiveId(next.length ? next[0].id : null);
      }
      return next;
    });
  };

  const resetForm = () => {
    files.forEach((f) => {
      if (f.previewURL) URL.revokeObjectURL(f.previewURL);
    });
    setFiles([]);
    setActiveId(null);
    setErrors({});
    setBatchProgress({ running: false, current: 0, total: 0, currentTitle: "" });
  };

  const validate = () => {
    const e = {};
    if (!files.length) e.files = t("err_add_at_least_one");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildFormData = (entry, visibilityOverride = null) => {
    const formData = new FormData();
    formData.append("media", entry.file);

    const safeTitle = (entry.title || fileNameSansExt(entry.file?.name) || "").trim() || t("untitled");
    formData.append("title", safeTitle);

    // Tags envoyés comme "location"
    formData.append("location", (entry.tags || "").trim());

    if (entry.description && entry.description.trim()) {
      formData.append("description", entry.description.trim());
    }

    formData.append("visibility", visibilityOverride || entry.visibility || "PUBLIC");
    return formData;
  };

  const parseModeration = (result) => {
    const m = result?.moderation || null;
    const decision = String(m?.decision || "allow").toLowerCase();

    return {
      decision,
      moderation: m,
      aiDecision: m?.decision || null,
      aiRiskScore: typeof m?.riskScore === "number" ? m.riskScore : null,
      aiFlags: Array.isArray(m?.flags) ? m.flags : [],
      aiReasons: Array.isArray(m?.reasons) ? m.reasons : [],
      aiContentTypes: Array.isArray(m?.contentType) ? m.contentType : [],
      aiSummary: m?.summary || "",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToastError("");
    setToastSuccess("");

    if (!token) {
      setToastError(t("err_need_login"));
      return;
    }
    if (!validate()) return;

    try {
      setIsUploading(true);

      const createdIds = [];
      let hadAnyIssue = false;

      const pending = files.filter((f) => !f.uploadedId);
      setBatchProgress({
        running: pending.length > 0,
        current: 0,
        total: pending.length,
        currentTitle: "",
      });

      let done = 0;

      for (const entry of files) {
        if (entry.uploadedId) continue;

        done += 1;
        setBatchProgress((p) => ({
          ...p,
          running: true,
          current: done,
          total: p.total || pending.length || 1,
          currentTitle: entry.title || fileNameSansExt(entry.file?.name) || t("untitled"),
        }));

        // Mark scanning (UX: "Gemini LLM" analysis running)
        setFiles((prev) =>
          prev.map((f) =>
            f.id === entry.id
              ? {
                  ...f,
                  aiStatus: "scanning",
                  aiDecision: null,
                  aiRiskScore: null,
                  aiFlags: [],
                  aiReasons: [],
                  aiContentTypes: [],
                  aiSummary: "",
                  uploadError: "",
                  // keep lock state until we know result
                  visibilityLocked: f.visibilityLocked || false,
                  visibilityLockReason: f.visibilityLockReason || "",
                }
              : f
          )
        );

        let result = null;
        let res = null;
        let retriedAsPrivate = false;

        const tryUpload = async (visibilityOverride) => {
          const formData = buildFormData(entry, visibilityOverride);
          const r = await fetch("/upload", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
          let json = null;
          try {
            json = await r.json();
          } catch (_) {
            json = { error: t("err_invalid_server_response") };
          }
          return { r, json };
        };

        try {
          // First attempt: user's chosen visibility
          const first = await tryUpload(null);
          res = first.r;
          result = first.json;

          // If server rejects because PUBLIC non compliant, retry as PRIVATE
          const pm = parseModeration(result);
          const shouldRetryPrivate =
            !res.ok &&
            (pm.decision === "block" || pm.decision === "review") &&
            String(entry.visibility || "PUBLIC").toUpperCase() === "PUBLIC";

          if (shouldRetryPrivate) {
            retriedAsPrivate = true;
            const second = await tryUpload("PRIVATE");
            res = second.r;
            result = second.json;
          }
        } catch (networkErr) {
          console.error("[CreateMedia] Network error:", networkErr);
          hadAnyIssue = true;
          setFiles((prev) =>
            prev.map((f) =>
              f.id === entry.id
                ? { ...f, aiStatus: "error", uploadError: t("err_network_retry") }
                : f
            )
          );
          continue;
        }

        const pmFinal = parseModeration(result);
        const decision = pmFinal.decision; // allow | review | block | error

        // UX rule: if not allow => Public disabled, allow private
        const restrictPublic = decision === "review" || decision === "block";

        if (!res.ok) {
          console.error("[CreateMedia] Upload error:", result);
          hadAnyIssue = true;

          // Update AI recap even on error responses (when provided)
          setFiles((prev) =>
            prev.map((f) =>
              f.id === entry.id
                ? {
                    ...f,
                    aiStatus: decision === "allow" ? "allow" : decision,
                    aiDecision: pmFinal.aiDecision,
                    aiRiskScore: pmFinal.aiRiskScore,
                    aiFlags: pmFinal.aiFlags,
                    aiReasons: pmFinal.aiReasons,
                    aiContentTypes: pmFinal.aiContentTypes,
                    aiSummary: pmFinal.aiSummary,
                    uploadError: result?.error || t("err_upload_failed"),
                    visibilityLocked: restrictPublic ? true : f.visibilityLocked,
                    visibilityLockReason: restrictPublic
                      ? t("reason_public_disabled")
                      : f.visibilityLockReason,
                    visibility: restrictPublic ? "PRIVATE" : f.visibility,
                  }
                : f
            )
          );

          continue;
        }

        // Success - but may still be review/block (Public disabled)
        if (decision === "review" || decision === "block") hadAnyIssue = true;

        setFiles((prev) =>
          prev.map((f) =>
            f.id === entry.id
              ? {
                  ...f,
                  uploadedId: result?.id || null,
                  uploadedAt: new Date().toISOString(),
                  aiStatus: decision === "allow" ? "allow" : decision,
                  aiDecision: pmFinal.aiDecision,
                  aiRiskScore: pmFinal.aiRiskScore,
                  aiFlags: pmFinal.aiFlags,
                  aiReasons: pmFinal.aiReasons,
                  aiContentTypes: pmFinal.aiContentTypes,
                  aiSummary: pmFinal.aiSummary,
                  uploadError: "",
                  // force PRIVATE if restricted (and especially if retried)
                  visibility: restrictPublic ? "PRIVATE" : f.visibility,
                  visibilityLocked: restrictPublic ? true : f.visibilityLocked,
                  visibilityLockReason: restrictPublic
                    ? t("err_public_disabled_toast")
                    : f.visibilityLockReason,
                }
              : f
          )
        );

        if (result?.id) {
          createdIds.push(result.id);
        }

        // If we had to retry as private, surface UX hint
        if (retriedAsPrivate) {
          setToastError(
            t("toast_saved_as_private")
          );
        }
      }

      setBatchProgress((p) => ({ ...p, running: false }));

      // Navigation rule requested:
      // - If everything is OK => navigate
      // - Else => stay and show recap (with Private enforcement)
      if (!hadAnyIssue) {
        setToastSuccess(files.length > 1 ? t("toast_published_multi") : t("toast_published_single"));
        setTimeout(() => setToastSuccess(""), 3500);

        resetForm();

        if (createdIds.length) {
          navigate("/myMedias", { state: { highlightIds: createdIds, fromCreateMedia: true } });
        } else {
          navigate("/myMedias", { state: { fromCreateMedia: true } });
        }
      } else {
        // Keep user here to see AI recap for each media
        setToastError(
          t("toast_analysis_done_some_blocked")
        );
        focusAiRecap();

        if (createdIds.length) {
          setToastSuccess(t("toast_some_added_private"));
          setTimeout(() => setToastSuccess(""), 4500);
        }
      }
    } catch (err) {
      console.error("[CreateMedia] Exception upload:", err);
      setToastError(err?.message || t("err_network_retry"));
    } finally {
      setIsUploading(false);
      setBatchProgress((p) => ({ ...p, running: false }));
    }
  };

  const activeMeta = activeEntry ? aiMetaFor(activeEntry) : null;
  const activeRisk = normalizeRiskScore(activeEntry?.aiRiskScore);

  const publicDisabled = activeEntry
    ? !!activeEntry.visibilityLocked || isRestrictedForPublic(activeEntry)
    : false;

  return (
    <div className="cm-page">
      <style>{createMediaStyles}</style>

      {/* TOASTS */}
      {toastSuccess && (
        <div className="cm-toast cm-toast-success" role="status">
          <i className="bi bi-check-circle-fill" />
          <span>{toastSuccess || t("toast_done_default")}</span>
          <button onClick={() => setToastSuccess("")} aria-label={t("close")}>
            ×
          </button>
        </div>
      )}
      {toastError && (
        <div className="cm-toast cm-toast-error" role="alert">
          <i className="bi bi-exclamation-octagon-fill" />
          <span>{toastError}</span>
          <button onClick={() => setToastError("")} aria-label={t("close")}>
            ×
          </button>
        </div>
      )}

      <div className="container-fluid">
        <div className="card border-0 rounded-4 shadow-sm">
          <div className="card-body">
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3">
              <div>
                <h4 className="mb-1 d-flex align-items-center gap-2">
                  <i className="bi bi-collection-play text-primary"></i>
                  {t("header_title")}
                </h4>
                <p className="text-muted mb-0">
                  {t("header_sub")}
                  <span className="d-block mt-1">
                    <i className="bi bi-shield-check me-1 text-primary"></i>
                    {t("header_ai_line", { engine: AI_ENGINE })}
                  </span>
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-4 align-items-start">
                {/* LEFT: Preview + dropzone */}
                <div className="col-12 col-xl-5">
                  {primary && (
                    <div className="cm-primary-preview mb-2">
                      {primary.kind === "image" ? (
                        <img src={primary.previewURL} alt={primary.title || t("alt_preview")} />
                      ) : (
                        <video src={primary.previewURL} controls playsInline preload="metadata" />
                      )}
                    </div>
                  )}

                  <div
                    ref={dropRef}
                    className="cm-dropzone mt-2"
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="cm-drop-icon text-primary">
                      <i className="bi bi-cloud-arrow-up"></i>
                    </div>
                    <div className="cm-drop-main">{t("drop_main")}</div>
                    <div className="cm-drop-sub mt-1">
                      {t("drop_or")}{" "}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill px-3"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        {t("drop_choose")}
                      </button>
                      <span className="d-block mt-1">
                        {t("drop_limits", { img: MAX_IMAGE_MB, vid: MAX_VIDEO_MB })}
                      </span>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      className="d-none"
                      onChange={handleFileInputChange}
                    />
                  </div>

                  {errors.files && <div className="cm-error mt-2">{errors.files}</div>}
                </div>

                {/* CENTER: Details */}
                <div className="col-12 col-xl-4">
                  {/* Batch progress (global) */}
                  {batchProgress.running && (
                    <div className="cm-batchbar" aria-live="polite">
                      <div className="cm-batchbar-top">
                        <div className="cm-batchbar-title">
                          <i className="bi bi-cpu" />
                          {t("batch_title")}
                        </div>
                        <span className="badge bg-light text-dark">
                          {batchProgress.current}/{batchProgress.total}
                        </span>
                      </div>
                      <div className="cm-batchbar-sub">
                        <span className="text-muted">{t("batch_in_progress")}</span> {batchProgress.currentTitle || t("dash")}{" "}
                        <span className="ms-2">·</span>{" "}
                        <span className="ms-2">
                          {t("batch_engine_info", { engine: AI_ENGINE })}
                        </span>
                      </div>
                      <div className="cm-batchbar-meter" aria-hidden="true">
                        <span
                          style={{
                            width: `${
                              batchProgress.total ? Math.round((batchProgress.current / batchProgress.total) * 100) : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="cm-title" className="form-label">
                      {t("label_title")}
                    </label>
                    <input
                      id="cm-title"
                      type="text"
                      className="form-control"
                      placeholder={t("ph_title")}
                      value={activeEntry?.title || ""}
                      onChange={(ev) => updateActiveField("title", ev.target.value)}
                      disabled={!activeEntry}
                    />
                    {!activeEntry && (
                      <small className="text-muted">
                        {t("hint_select_media")}
                      </small>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="cm-description" className="form-label">
                      {t("label_description")}
                    </label>
                    <textarea
                      id="cm-description"
                      className="form-control"
                      rows={4}
                      placeholder={t("ph_description")}
                      value={activeEntry?.description || ""}
                      onChange={(ev) => updateActiveField("description", ev.target.value)}
                      disabled={!activeEntry}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="cm-tags" className="form-label">
                      <i className="bi bi-tags me-1" />
                      {t("label_tags")}
                    </label>
                    <input
                      id="cm-tags"
                      className="form-control cm-tags-input"
                      placeholder={t("ph_tags")}
                      value={activeEntry?.tags || ""}
                      onChange={(ev) => updateActiveField("tags", ev.target.value)}
                      disabled={!activeEntry}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label d-block mb-1">{t("label_visibility")}</label>

                    <div className="cm-vis-toggle">
                      <button
                        type="button"
                        className={"cm-vis-btn " + (activeEntry?.visibility === "PUBLIC" ? "active-public" : "")}
                        onClick={() => updateActiveField("visibility", "PUBLIC")}
                        disabled={!activeEntry || publicDisabled}
                      >
                        <i className="bi bi-globe2" />
                        <span>{t("visibility_public")}</span>
                      </button>

                      <button
                        type="button"
                        className={"cm-vis-btn " + (activeEntry?.visibility === "PRIVATE" ? "active-private" : "")}
                        onClick={() => updateActiveField("visibility", "PRIVATE")}
                        disabled={!activeEntry}
                      >
                        <i className="bi bi-lock-fill" />
                        <span>{t("visibility_private")}</span>
                      </button>
                    </div>

                    <div className="form-text">
                      {t("visibility_help")}
                      {activeEntry && publicDisabled && (
                        <span className="d-block mt-1" style={{ color: "#92400e" }}>
                          <i className="bi bi-shield-lock me-1" />
                          {t("public_disabled_note")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* AI Content Guard (Gemini) */}
                  {activeEntry && (
                    <div
                      ref={aiCardRef}
                      className={"cm-ai-card mb-4 " + (aiFlash ? "cm-ai-card-flash" : "")}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-shield-check text-primary" />
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{t("ai_card_title")}</div>
                        </div>

                        {activeMeta && (
                          <span className={activeMeta.cls}>
                            <span className="cm-ai-dot" style={{ background: activeMeta.dot }} />
                            {activeMeta.label}
                          </span>
                        )}
                      </div>

                      <div className="cm-ai-subline">
                        <div className="cm-ai-mini">
                          {t("ai_card_subline")}
                        </div>
                        <span className="cm-ai-engine">
                          <i className="bi bi-stars" />
                          {t("ai_engine_badge", { engine: AI_ENGINE })}
                        </span>
                      </div>

                      {/* Scanning UI */}
                      {String(activeEntry.aiStatus || "").toLowerCase() === "scanning" && (
                        <div className="mt-3">
                          <div className="cm-ai-callout warn">
                            <div className="d-flex align-items-start gap-2">
                              <i className="bi bi-hourglass-split" style={{ marginTop: 2 }} />
                              <div>
                                <div style={{ fontWeight: 700 }}>{t("ai_scanning_title", { engine: AI_ENGINE })}</div>
                                <div className="small mt-1">
                                  {t("ai_scanning_desc")}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="cm-ai-progress" aria-hidden="true">
                              <span />
                            </div>
                            <div className="small text-muted mt-2">
                              {t("ai_steps", { engine: AI_ENGINE })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Verdict recap */}
                      {String(activeEntry.aiStatus || "").toLowerCase() !== "scanning" &&
                        String(activeEntry.aiStatus || "").toLowerCase() !== "idle" && (
                          <div className="mt-3">
                            {String(activeEntry.aiStatus || "").toLowerCase() === "allow" && (
                              <div className="cm-ai-callout ok">
                                <div className="d-flex align-items-start gap-2">
                                  <i className="bi bi-check2-circle" style={{ marginTop: 2 }} />
                                  <div>
                                    <div style={{ fontWeight: 800 }}>{t("ai_allow_title")}</div>
                                    <div className="small mt-1">
                                      {t("ai_allow_desc", { engine: AI_ENGINE })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {(String(activeEntry.aiStatus || "").toLowerCase() === "review" ||
                              String(activeEntry.aiStatus || "").toLowerCase() === "block") && (
                              <div className="cm-ai-callout danger">
                                <div className="d-flex align-items-start gap-2">
                                  <i className="bi bi-shield-lock" style={{ marginTop: 2 }} />
                                  <div>
                                    <div style={{ fontWeight: 800 }}>
                                      {t("ai_block_title")}
                                    </div>
                                    <div className="small mt-1">
                                      {t("ai_block_desc_prefix")} <strong>{t("visibility_private")}</strong>.
                                      {t("ai_block_desc_suffix")}
                                    </div>

                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                      <button
                                        type="button"
                                        className="btn btn-dark btn-sm rounded-pill px-3"
                                        onClick={() => {
                                          lockToPrivate(activeEntry.id, activeEntry.visibilityLockReason || "");
                                          setToastSuccess(t("toast_set_private_ok"));
                                          setTimeout(() => setToastSuccess(""), 2200);
                                        }}
                                        disabled={activeEntry.visibility === "PRIVATE"}
                                      >
                                        <i className="bi bi-lock-fill me-1" />
                                        {t("btn_set_private")}
                                      </button>

                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                                        onClick={() => navigate("/myMedias", { state: { fromCreateMedia: true } })}
                                      >
                                        <i className="bi bi-folder2-open me-1" />
                                        {t("btn_view_my_media")}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* KPI boxes */}
                            <div className="cm-ai-kv">
                              <div className="cm-ai-kv-box">
                                <div className="cm-ai-kv-title">{t("kpi_decision")}</div>
                                <div className="cm-ai-kv-value">{activeEntry.aiDecision || "—"}</div>
                              </div>
                              <div className="cm-ai-kv-box">
                                <div className="cm-ai-kv-title">{t("kpi_risk")}</div>
                                <div className="cm-ai-kv-value">
                                  {activeRisk === null ? "—" : `${activeRisk}%`}
                                </div>
                                {activeRisk !== null && (
                                  <div className="progress mt-2" style={{ height: 8, borderRadius: 999 }}>
                                    <div
                                      className="progress-bar"
                                      role="progressbar"
                                      style={{ width: `${activeRisk}%` }}
                                      aria-valuenow={activeRisk}
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Details */}
                            <div className="mt-3">
                              {!!activeEntry.aiSummary && (
                                <div className="mb-2">
                                  <div className="small text-muted" style={{ fontWeight: 700 }}>
                                    {t("ai_summary_title", { engine: AI_ENGINE })}
                                  </div>
                                  <div className="small" style={{ color: "#0f172a" }}>
                                    {activeEntry.aiSummary}
                                  </div>
                                </div>
                              )}

                              {!!activeEntry.aiReasons?.length && (
                                <div className="mb-2">
                                  <div className="small text-muted" style={{ fontWeight: 700 }}>
                                    {t("ai_points_title")}
                                  </div>
                                  <ul className="small mb-0" style={{ color: "#0f172a", paddingLeft: "1.05rem" }}>
                                    {activeEntry.aiReasons.slice(0, 6).map((r, idx) => (
                                      <li key={`${idx}-${r}`}>{r}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {!!activeEntry.aiFlags?.length && (
                                <div className="mb-2">
                                  <div className="small text-muted" style={{ fontWeight: 700 }}>
                                    {t("ai_flags_title")}
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    {activeEntry.aiFlags.slice(0, 10).map((t) => (
                                      <span key={t} className="cm-ai-pill">
                                        <i className="bi bi-flag text-muted" />
                                        {t}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {!!activeEntry.aiContentTypes?.length && (
                                <div className="mb-2">
                                  <div className="small text-muted" style={{ fontWeight: 700 }}>
                                    {t("ai_suggested_tags_title", { engine: AI_ENGINE })}
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    {activeEntry.aiContentTypes.slice(0, 10).map((t) => (
                                      <span key={t} className="cm-ai-pill">
                                        <i className="bi bi-hash text-muted" />
                                        {t}
                                      </span>
                                    ))}
                                  </div>

                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                      onClick={applySuggestedTags}
                                      disabled={!activeEntry}
                                    >
                                      <i className="bi bi-magic me-1" />
                                      {t("btn_apply_suggested_tags")}
                                    </button>
                                  </div>
                                </div>
                              )}

                              {!!activeEntry.uploadError && (
                                <div className="cm-error mt-2">
                                  <i className="bi bi-exclamation-triangle me-1" />
                                  {activeEntry.uploadError}
                                </div>
                              )}

                              {publicDisabled && (
                                <div className="small text-muted mt-3">
                                  <i className="bi bi-info-circle me-1" />
                                  {t("reminder_prefix")} <strong>{t("visibility_private")}</strong> {t("reminder_suffix")}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      {/* Idle state */}
                      {String(activeEntry.aiStatus || "").toLowerCase() === "idle" && (
                        <div className="mt-3">
                          <div className="cm-ai-callout">
                            <div className="d-flex align-items-start gap-2">
                              <i className="bi bi-lightning-charge" style={{ marginTop: 2 }} />
                              <div>
                                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                  {t("ai_idle_title", { engine: AI_ENGINE })}
                                </div>
                                <div className="small mt-1 text-muted">
                                  {t("ai_idle_desc_1", { engine: AI_ENGINE })}
                                  {t("ai_idle_desc_2")}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Footer actions */}
                  <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between mt-4 gap-3">
                    <div className="text-muted small text-center text-md-start">
                      {files.length
                        ? (lang === "fr"
                            ? t("footer_ready", {
                                count: files.length,
                                plural_m: files.length > 1 ? "s" : "",
                                plural_p: files.length > 1 ? "s" : "",
                                plural_b: files.length > 1 ? "s" : "",
                              })
                            : t("footer_ready", {
                                count: files.length,
                                plural: files.length === 1 ? "" : "s",
                              }))
                        : t("footer_add_to_start")}
                    </div>

                    <div className="d-flex flex-row flex-nowrap justify-content-end align-items-center gap-2 w-100 w-md-auto">
                      <button
                        type="button"
                        className="btn btn-outline-secondary rounded-pill px-3 w-auto text-nowrap"
                        onClick={resetForm}
                        disabled={!files.length || isUploading}
                      >
                        {t("btn_reset")}
                      </button>

                      <button
                        type="submit"
                        className="btn btn-primary rounded-pill px-4 w-auto text-nowrap"
                        disabled={!files.length || isUploading}
                      >
                        {isUploading && (
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                        )}
                        {files.length > 1 ? t("btn_publish_multi") : t("btn_publish_single")}
                      </button>
                    </div>
                  </div>
                </div>

                {/* RIGHT: medias list */}
                <div className="col-12 col-xl-3">
                  <div className="cm-drafts-panel">
                    <div className="cm-drafts-header">
                      <div>
                        <div className="cm-drafts-title">{t("right_title")}</div>
                        <div className="cm-drafts-sub">{t("right_sub")}</div>
                      </div>
                      <span className="badge bg-light text-dark cm-drafts-count">
                        {t("right_count", { count: files.length, plural: files.length > 1 ? "s" : "" })}
                      </span>
                    </div>

                    <div className="cm-file-list">
                      {files.length === 0 ? (
                        <div className="text-muted small px-1 py-2">
                          {t("right_empty")}
                        </div>
                      ) : (
                        files.map((entry) => {
                          const meta = aiMetaFor(entry);
                          const restricted = !!entry.visibilityLocked || isRestrictedForPublic(entry);

                          return (
                            <div
                              key={entry.id}
                              className={
                                "cm-file-item d-flex align-items-center justify-content-between" +
                                (entry.id === activeId ? " active" : "")
                              }
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setActiveId(entry.id);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(ev) => {
                                if (ev.key === "Enter" || ev.key === " ") setActiveId(entry.id);
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 flex-grow-1">
                                <div className="cm-file-thumb">
                                  {entry.kind === "image" ? (
                                    <img src={entry.previewURL} alt={entry.title || "thumb"} />
                                  ) : (
                                    <video src={entry.previewURL} muted playsInline preload="metadata" />
                                  )}
                                </div>

                                <div className="cm-file-item-main">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="cm-file-name">
                                      {entry.title || fileNameSansExt(entry.file?.name)}
                                    </div>
                                    <small className="cm-file-meta ms-2">{entry.sizeMB.toFixed(1)} {t("unit_mb")}</small>
                                  </div>

                                  <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                    <span className="badge bg-light text-muted">
                                      {entry.kind === "video" ? t("badge_video") : t("badge_image")}
                                    </span>

                                    <span className={meta.cls}>
                                      <span className="cm-ai-dot" style={{ background: meta.dot }} />
                                      {meta.label}
                                      {String(entry.aiStatus || "").toLowerCase() === "scanning" && (
                                        <span className="ms-1 spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                                      )}
                                    </span>

                                    {restricted && (
                                      <span className="badge bg-warning text-dark">
                                        <i className="bi bi-shield-lock me-1" />
                                        {t("badge_public_off")}
                                      </span>
                                    )}

                                    <span
                                      className={
                                        "badge rounded-pill " +
                                        ((restricted || entry.visibility === "PRIVATE") ? "bg-dark" : "bg-primary")
                                      }
                                    >
                                      {(restricted || entry.visibility === "PRIVATE") ? t("visibility_private") : t("visibility_public")}
                                    </span>

                                    <button
                                      type="button"
                                      className="cm-info-btn"
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        setActiveId(entry.id);
                                        focusAiRecap();
                                      }}
                                      aria-label={t("aria_view_ai", { engine: AI_ENGINE })}
                                    >
                                      <i className="bi bi-info-circle" />
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary ms-2"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleRemoveFile(entry.id);
                                }}
                                aria-label={t("aria_remove_file")}
                              >
                                <i className="bi bi-x-lg" />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMedia;
